import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { callApi, fnGetDataSet } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import SnResizeBar from "@shared/components/SnResizeBar";
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface

interface Props {
  Menuprops: menuInfo;
}

/** 상단 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid1";

/** 집계 버튼의 cd_param. sys_headerctrl 행과 맞춰야 한다. */
const BTN_AGGREGATE = "agg";

const SN_COS_MAS_REG_006 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(), // 직접재료비 (품목별) — cos_matlamh
    grid2: React.createRef(), // 직접재료비내역 (자재별) — cos_matlaml
  });
  const { userInfo } = global();
  /** 기간 도움창 선택 → 자동조회. 시작/종료가 연달아 바뀌므로 마지막 것만 실행한다. */
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({ grid1: [], grid2: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [], grid2: [] });
  /** 상단 버튼 메타. getInitForm 이 이미 {controls, data} 로 가공해 주므로 다시 감싸지 않는다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
  //#endregion

  //#region  공통 유틸
  const getSearchForm = () => searchRef.current?.getFormdata() ?? {};

  /** 원가기간 시작/종료월. SnPeriodMonthBoxPop 이 YYYYMM 을 준다. */
  const getYm = () => {
    const form = getSearchForm();
    return {
      ym_start: String(form.dt_from ?? "").substring(0, 6),
      ym_end: String(form.dt_to ?? "").substring(0, 6),
    };
  };
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid?.grid1 ?? [],
          grid2: rtn.grid?.grid2 ?? [],
        });
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    try {
      const form = getSearchForm();
      const { ym_start, ym_end } = getYm();

      setDataSources({ grid1: [], grid2: [] });

      const data: any = await fnGetDataSet({
        id: "sn_cos_mas_reg_006_01",
        params: { ...form, ym_start, ym_end },
      });

      // 내역(grid2)에는 전체를 넣는다. 품목을 고르면 grid1 의 filter 가
      // grid2.applyFilters({cd_item}) 를 호출해 그리드 안에서 걸러진다.
      setDataSources({ grid1: data?.[0] ?? [], grid2: data?.[1] ?? [] });
    } catch (error: any) {}
  };

  /**
   * 기간 도움창에서 기간을 고르면 바로 조회한다.
   * source 가 'popup' 일 때만 반응한다 — 직접 입력 중에는 조회하지 않는다.
   * (조회조건만 바뀌고 그리드는 이전 자료가 남아 있으면 오해를 부른다)
   */
  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  /** 상단 버튼(sys_headerctrl)의 cd_param 이 sender 로 넘어온다. */
  const onBtnEtcClick = async (sender: string) => {
    if (sender !== BTN_AGGREGATE) return;

    const form = getSearchForm();
    const { ym_start, ym_end } = getYm();

    if (!form.cd_biz) {
      await showDialog("alter", "사업장을 선택해주세요.");
      return;
    }
    if (ym_start === "" || ym_end === "") {
      await showDialog("alter", "원가기간을 선택해주세요.");
      return;
    }

    try {
      // 이미 집계된 자료가 있으면 다시 묻는다. SP 가 해당 기간을 지우고 다시 만든다.
      const rows: any = await fnGetDataSet({
        id: "sn_cos_mas_reg_006_05",
        params: { cd_firm: userInfo.cd_firm, cd_biz: form.cd_biz, ym_end },
      });

      if (Number(rows?.[0]?.[0]?.cnt ?? 0) > 0) {
        const confirm = await showDialog(
          "confirm",
          "이미 처리된 내역이 있습니다. 삭제하고 다시 생성하겠습니까?"
        );
        if (!confirm) return;
      }

      const ok = await callApi("SN_COS_MAS_REG_006_Btn", {
        cd_firm: userInfo.cd_firm,
        cd_biz: form.cd_biz,
        ym_start,
        ym_end,
      });
      // 집계만 하고 화면이 그대로면 됐는지 알 수 없다. 끝나면 바로 다시 조회한다.
      if (ok !== false) await onSearch();
    } catch (error: any) {}
  };
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>

      <div className="flex h-full min-h-0 gap-2">
        {/* 직접재료비 — 품목별. 선택한 품목이 우측 내역의 필터가 된다. */}
        <SnResizeBar direction={"width"} id="panel1" orgSize="50%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={columns.grid1}
            rowData={dataSources.grid1}
            etcProps={{
              isEditable: false,
              rowSelection: "single",
              title: "직접재료비",
            }}
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["cd_item"],
            }}
            events={{ onBtnEtcClick }} // 집계 (sys_headerctrl)
            headerInfo={headerInfo}
          />
        </SnResizeBar>

        {/* 직접재료비내역 — 자재별. 전체를 받아두고 grid1 선택으로 걸러진다. */}
        <div className="flex flex-1 flex-col min-h-0">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={columns.grid2}
            rowData={dataSources.grid2}
            etcProps={{
              isEditable: false,
              rowSelection: "single",
              title: "직접재료비내역",
              // 좌측에서 행을 고르면 filter prop 이 이 그리드의 applyFilters 를 부르고,
              // 그 끝에서 selectAndFocusRow(0) 가 돌아 포커스가 우측으로 넘어간다.
              // 행 선택·스크롤만 하고 DOM 포커스는 좌측에 남긴다.
              suppressInitialCellFocus: true,
            }}
          />
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_006;
